import { memo } from "react";
import { styled } from "@mui/material/styles";
import { readJournalTitle } from "@shared/journal";
import { ShieldBadge } from "../../../primitives/data-display/ShieldBadge";

export interface DocumentMetadataBadgesProps {
  /** The document's bibliographic metadata (sources_health.metadata). */
  metadata: Record<string, unknown> | null | undefined;
}

interface MetaPair {
  key: string;
  value: string;
}

function asStringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string" && !!v) : [];
}

const BadgeRow = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(0.75),
  marginTop: theme.spacing(0.75),
}));

/**
 * Compact key/value flow of the annotation-relevant document metadata: journal and funding
 * agencies — the two fields that bear on scorecard judgments (peer-review, funding). Each pair is
 * a shields.io-style badge (dark key tag glued to a lighter value) that wraps as a whole, so the
 * key never separates from its value. Renders nothing when neither field is present.
 */
export const DocumentMetadataBadges = memo(function DocumentMetadataBadges({
  metadata,
}: DocumentMetadataBadgesProps) {
  if (!metadata) return null;

  const journal = readJournalTitle(metadata);
  const agencies = [...new Set(asStringList(metadata.grant_agencies))];

  const pairs: MetaPair[] = [
    ...(journal ? [{ key: "Journal", value: journal }] : []),
    ...agencies.map((a) => ({ key: "Funding", value: a })),
  ];
  if (pairs.length === 0) return null;

  return (
    <BadgeRow>
      {pairs.map((pair, i) => (
        <ShieldBadge key={`${pair.key}-${pair.value}-${i}`} label={pair.key} value={pair.value} />
      ))}
    </BadgeRow>
  );
});
